import { useState, useMemo } from "react";
// Constantes normativas 2026 (Perú)
const RMV_2026 = 1130;
const RMN_2026 = RMV_2026 * 1.35; // Remuneración Mínima Nocturna (RMV + 35%)

const MODOS = [
  { id: "extras", etiqueta: "Horas extra", icono: "⏰" },
  { id: "nocturno", etiqueta: "Trabajo nocturno", icono: "🌙" },
  { id: "feriado", etiqueta: "Feriado / descanso", icono: "📅" },
];

function formatearSoles(valor) {
  return valor.toLocaleString("es-PE", {
    style: "currency",
    currency: "PEN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function CalculadoraHorasExtras() {
  const [modo, setModo] = useState("extras");
  const [sueldoBruto, setSueldoBruto] = useState(3000);
  const [horasJornada, setHorasJornada] = useState(8);

  // Horas extra
  const [horasExtra, setHorasExtra] = useState(3);

  // Trabajo nocturno
  const [esJornadaNocturnaCompleta, setEsJornadaNocturnaCompleta] = useState(true);
  const [horasNocturnasExtra, setHorasNocturnasExtra] = useState(2);

  // Feriado / descanso
  const [diasFeriadoTrabajados, setDiasFeriadoTrabajados] = useState(1);
  const [conDescansoSustitutorio, setConDescansoSustitutorio] = useState(false);

  const resultado = useMemo(() => {
    const bruto = Number(sueldoBruto) || 0;
    const jornada = Number(horasJornada) || 8;
    const valorDia = bruto / 30;
    const valorHora = valorDia / jornada;

    if (modo === "extras") {
      const horas = Math.max(0, Number(horasExtra) || 0);
      const primeras2 = Math.min(2, horas);
      const restantes = Math.max(0, horas - 2);
      const pagoPrimeras2 = valorHora * 1.25 * primeras2;
      const pagoRestantes = valorHora * 1.35 * restantes;
      const total = pagoPrimeras2 + pagoRestantes;
      return {
        valorHora,
        total,
        detalle: [
          { etiqueta: `Primeras ${primeras2} h (25%)`, monto: pagoPrimeras2 },
          { etiqueta: `Horas adicionales ${restantes} h (35%)`, monto: pagoRestantes },
        ],
      };
    }

    if (modo === "nocturno") {
      const rmnMensual = RMN_2026;
      const diferenciaMinima = esJornadaNocturnaCompleta ? Math.max(0, rmnMensual - bruto) : 0;

      const horas = Math.max(0, Number(horasNocturnasExtra) || 0);
      const valorHoraNocturna = Math.max(valorHora, rmnMensual / 30 / jornada);
      const primeras2 = Math.min(2, horas);
      const restantes = Math.max(0, horas - 2);
      const pagoHorasExtraNocturnas =
        valorHoraNocturna * 1.25 * primeras2 + valorHoraNocturna * 1.35 * restantes;

      const total = diferenciaMinima + pagoHorasExtraNocturnas;
      return {
        valorHora,
        rmnMensual,
        diferenciaMinima,
        total,
        detalle: [
          ...(esJornadaNocturnaCompleta
            ? [{ etiqueta: "Diferencial a RMN (jornada 100% nocturna)", monto: diferenciaMinima }]
            : []),
          ...(horas > 0
            ? [{ etiqueta: `Horas extra en horario nocturno (${horas} h)`, monto: pagoHorasExtraNocturnas }]
            : []),
        ],
      };
    }

    // feriado
    const dias = Math.max(0, Number(diasFeriadoTrabajados) || 0);
    const total = conDescansoSustitutorio ? 0 : valorDia * dias; 
    return {
      valorHora,
      valorDia,
      total,
      detalle: conDescansoSustitutorio
        ? [{ etiqueta: "Con descanso sustitutorio otorgado: sin recargo económico", monto: 0 }]
        : [{ etiqueta: `Recargo del 100% (${dias} día(s))`, monto: total }],
    };
  }, [
    modo,
    sueldoBruto,
    horasJornada,
    horasExtra,
    esJornadaNocturnaCompleta,
    horasNocturnasExtra,
    diasFeriadoTrabajados,
    conDescansoSustitutorio,
  ]);

  const inputClase =
    "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700";

  return (
    <div className="w-full min-h-full px-4 py-10 bg-stone-50">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center justify-center text-lg text-white bg-red-700 rounded-full h-11 w-11">
            ⏰
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">
              Calculadora de Horas Extras
            </h1>
            <p className="text-sm text-slate-500">Perú 2026 · 25%, 35%, recargo nocturno y feriados</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-1.5 mb-4">
          {MODOS.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setModo(m.id)}
              className={`rounded-lg border py-2.5 px-1 text-xs font-medium transition-colors flex flex-col items-center gap-1 ${
                modo === m.id
                  ? "border-red-700 bg-red-50 text-red-800"
                  : "border-slate-300 text-slate-600 hover:bg-slate-50 bg-white"
              }`}
            >
              <span className="text-base">{m.icono}</span>
              {m.etiqueta}
            </button>
          ))}
        </div>

        <div className="overflow-hidden bg-white border shadow-sm rounded-2xl border-slate-200">
          <div className="p-6 space-y-5 border-b border-slate-100">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Sueldo bruto mensual
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-sm">
                    S/
                  </span>
                  <input
                    type="number"
                    min="0"
                    value={sueldoBruto}
                    onChange={(e) => setSueldoBruto(e.target.value)}
                    className={`${inputClase} pl-9`}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Horas de jornada diaria
                </label>
                <input
                  type="number"
                  min="1"
                  max="12"
                  value={horasJornada}
                  onChange={(e) => setHorasJornada(e.target.value)}
                  className={inputClase}
                />
              </div>
            </div>

            {modo === "extras" && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Horas extra trabajadas (en un día)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={horasExtra}
                  onChange={(e) => setHorasExtra(e.target.value)}
                  className={inputClase}
                />
                <p className="mt-1.5 text-xs text-slate-400">
                  Las primeras 2 horas se pagan con 25% de recargo; a partir de la
                  tercera hora, con 35%.
                </p>
              </div>
            )}

            {modo === "nocturno" && (
              <div className="space-y-4">
                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={esJornadaNocturnaCompleta}
                    onChange={(e) => setEsJornadaNocturnaCompleta(e.target.checked)}
                    className="w-4 h-4 text-red-700 rounded border-slate-300 focus:ring-red-700/30"
                  />
                  <span className="text-sm text-slate-700">
                    Mi jornada regular es 100% nocturna (10 p.m. – 6 a.m.)
                  </span>
                </label>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Horas extra trabajadas en horario nocturno
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={horasNocturnasExtra}
                    onChange={(e) => setHorasNocturnasExtra(e.target.value)}
                    className={inputClase}
                  />
                </div>
                <p className="p-3 text-xs rounded-lg text-slate-400 bg-slate-50">
                  Si tu jornada regular es íntegramente nocturna, no puedes ganar menos
                  de la Remuneración Mínima Nocturna: {formatearSoles(RMN_2026)} (RMV +
                  35%). Las horas extra nocturnas mantienen el recargo de 25%/35% sobre
                  ese valor hora mínimo.
                </p>
              </div>
            )}

            {modo === "feriado" && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Días feriados o de descanso trabajados
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={diasFeriadoTrabajados}
                    onChange={(e) => setDiasFeriadoTrabajados(e.target.value)}
                    className={inputClase}
                  />
                </div>
                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={conDescansoSustitutorio}
                    onChange={(e) => setConDescansoSustitutorio(e.target.checked)}
                    className="w-4 h-4 text-red-700 rounded border-slate-300 focus:ring-red-700/30"
                  />
                  <span className="text-sm text-slate-700">
                    Me otorgaron un día de descanso sustitutorio
                  </span>
                </label>
                <p className="p-3 text-xs rounded-lg text-slate-400 bg-slate-50">
                  Si trabajas un feriado o tu día de descanso semanal sin que te den un
                  descanso sustitutorio, te corresponde un recargo del 100% además de
                  tu remuneración regular de ese día.
                </p>
              </div>
            )}
          </div>

          <div className="p-6 bg-slate-900">
            <p className="mb-1 text-xs tracking-wide uppercase text-slate-400">
              Pago adicional estimado
            </p>
            <p className="mb-5 font-mono text-4xl font-bold text-white">
              {formatearSoles(Math.max(0, resultado.total))}
            </p>

            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-300">
                <span>Valor hora ordinario</span>
                <span className="font-mono">{formatearSoles(resultado.valorHora)}</span>
              </div>
              {resultado.detalle.map((item, i) => (
                <div key={i} className="flex justify-between text-slate-300">
                  <span>{item.etiqueta}</span>
                  <span className="font-mono">{formatearSoles(item.monto)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <p className="mt-4 text-xs leading-relaxed text-slate-400">
          Herramienta orientativa. El valor hora se calcula como sueldo bruto ÷ (30
          días × horas de jornada diaria). No sustituye el registro de asistencia ni
          acuerdos particulares sobre jornada. No constituye asesoría legal, contable
          ni laboral.
        </p>
      </div>
    </div>
  );
}